import React, { useRef, useEffect, useCallback } from "react";

interface AudioVisualizerProps {
  analyserNode: AnalyserNode;
  barCount?: number;
  barWidth?: number;
  barGap?: number;
  barMinHeight?: number;
}

/** Read --color-primary from computed style and return [r, g, b]. */
function resolvePrimaryRgb(el: HTMLElement): [number, number, number] {
  const hex = getComputedStyle(el).getPropertyValue("--color-primary").trim();
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m
    ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)]
    : [184, 135, 46];
}

const AudioVisualizer: React.FC<AudioVisualizerProps> = ({
  analyserNode,
  barCount = 28,
  barWidth = 2,
  barGap = 1.5,
  barMinHeight = 2,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const smoothedRef = useRef<Float32Array | null>(null);
  const rafRef = useRef<number>(0);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const w = rect.width * dpr;
    const h = rect.height * dpr;

    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }

    ctx.clearRect(0, 0, w, h);

    // Resolve color from CSS custom property (respects light/dark mode)
    const [r, g, b] = resolvePrimaryRgb(canvas);

    // Read frequency data
    const bufferLength = analyserNode.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyserNode.getByteFrequencyData(dataArray);

    // Initialize smoothed values
    if (!smoothedRef.current || smoothedRef.current.length !== barCount) {
      smoothedRef.current = new Float32Array(barCount);
    }
    const smoothed = smoothedRef.current;

    const totalBarWidth = (barWidth + barGap) * dpr;
    const totalWidth = totalBarWidth * barCount - barGap * dpr;
    const startX = (w - totalWidth) / 2;
    const maxBarHeight = h * 0.85;

    for (let i = 0; i < barCount; i++) {
      // Map bar to frequency bin — weight toward lower frequencies for voice
      const binIndex = Math.min(
        Math.floor(Math.pow(i / barCount, 1.5) * bufferLength),
        bufferLength - 1,
      );
      const rawValue = dataArray[binIndex] / 255;

      // Smooth interpolation — snappy rise, graceful fall
      const lerp = rawValue > smoothed[i] ? 0.45 : 0.1;
      smoothed[i] += (rawValue - smoothed[i]) * lerp;

      const barH = Math.max(barMinHeight * dpr, smoothed[i] * maxBarHeight);
      const x = startX + i * totalBarWidth;
      const y = (h - barH) / 2;
      const bw = barWidth * dpr;
      const radius = bw / 2;

      const opacity = 0.25 + smoothed[i] * 0.75;

      // Vertical gradient — brightest at center, fading at tips
      const grad = ctx.createLinearGradient(x, y, x, y + barH);
      grad.addColorStop(0, `rgba(${r},${g},${b},${opacity * 0.35})`);
      grad.addColorStop(0.5, `rgba(${r},${g},${b},${opacity})`);
      grad.addColorStop(1, `rgba(${r},${g},${b},${opacity * 0.35})`);
      ctx.fillStyle = grad;

      ctx.beginPath();
      ctx.roundRect(x, y, bw, barH, radius);
      ctx.fill();
    }

    rafRef.current = requestAnimationFrame(draw);
  }, [analyserNode, barCount, barWidth, barGap, barMinHeight]);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [draw]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="audio-visualizer"
      aria-hidden="true"
      className="absolute inset-0 w-full h-full"
      style={{ display: "block" }}
    />
  );
};

export default AudioVisualizer;
